import { useEffect, useMemo, useState } from 'react';
import { Download } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import { Checkbox } from '../../../components/ui/checkbox';
import { buildRoutesSheet } from '../utils/exportRoutes';

const STORAGE_KEY = 'routes_export_excluded_columns';

const buildColumns = ({ firmMap, isMultiCustomer, getCustomerName }) => {
  const n = (v) => (v === null || v === undefined || v === '' ? '' : Number(v));
  return [
    { label: 'Tarih', get: (r) => r.date?.split('T')[0] || r.date || '', always: true },
    { label: 'Müşteri', get: (r) => getCustomerName(r.customer), always: isMultiCustomer },
    { label: 'Firma', get: (r) => r.firm_name || firmMap[r.firm_id] || '', always: true },
    { label: 'Route No', get: (r) => r.route_number || '' },
    { label: 'Tur Tipi', get: (r) => r.route_type || '' },
    { label: 'Rit/Plaats', get: (r) => r.plaats || r.rit_number || '' },
    { label: 'Plaka', get: (r) => r.license_plate || '', always: true },
    { label: 'Şoför', get: (r) => r.driver_name || '', always: true },
    { label: 'Yardımcı Şoför', get: (r) => r.copilot_name || '' },
    { label: 'KM Başlangıç', get: (r) => n(r.km_start), num: true },
    { label: 'KM Bitiş', get: (r) => n(r.km_end), num: true },
    { label: 'Toplam KM', get: (r) => n(r.km_total), num: true },
    { label: 'Stops', get: (r) => n(r.stops), num: true },
    { label: 'Saat Ücreti', get: (r) => n(r.hourly_rate), num: true },
    { label: 'Başlangıç / Stop Ücreti', get: (r) => n(r.start_cost), num: true },
    { label: 'Başlangıç Saati', get: (r) => r.start_time || '' },
    { label: 'Bitiş Saati', get: (r) => r.end_time || '' },
    { label: 'Mola', get: (r) => (r.break_time && r.break_time !== '00:00' ? r.break_time : '') },
    { label: 'Çalışma Saati', get: (r) => n(r.work_hours), num: true },
    { label: 'İş Maliyeti', get: (r) => n(r.work_cost), num: true },
    { label: 'Toplam', get: (r) => Number(r.total_cost || 0), num: true, always: true },
    { label: 'Notlar', get: (r) => r.notes || '' },
  ];
};

const loadExcluded = () => {
  try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]'); } catch { return []; }
};

// Tablodaki filtreli kayıtları, kullanıcının seçtiği kolonlarla Excel'e aktarır. Seçim hatırlanır.
export function ExportColumnsDialog({ open, onOpenChange, routes, firms, isMultiCustomer, getCustomerName }) {
  const [excluded, setExcluded] = useState(loadExcluded);
  const available = useMemo(() => {
    const firmMap = {};
    firms.forEach((f) => { firmMap[f.id] = f.name; });
    return buildColumns({ firmMap, isMultiCustomer, getCustomerName })
      .filter((c) => c.always || routes.some((r) => { const v = c.get(r); return v !== '' && v !== 0; }));
  }, [routes, firms, isMultiCustomer, getCustomerName]);

  useEffect(() => { if (open) setExcluded(loadExcluded()); }, [open]);

  const chosen = available.filter((c) => !excluded.includes(c.label));
  const toggle = (label) => setExcluded((ex) => (ex.includes(label) ? ex.filter((l) => l !== label) : [...ex, label]));
  const setAll = (on) => setExcluded((ex) => (on ? ex.filter((l) => !available.some((c) => c.label === l)) : [...new Set([...ex, ...available.map((c) => c.label)])]));

  const download = async () => {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(excluded)); } catch {}
    const headers = chosen.map((c) => c.label);
    const rows = routes.map((r) => chosen.map((c) => c.get(r)));
    const numericCols = chosen.map((c, i) => (c.num ? i : -1)).filter((i) => i >= 0);
    const XLSX = await import('xlsx');
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, buildRoutesSheet(XLSX, headers, rows, numericCols), 'Routes');
    XLSX.writeFile(wb, `filtered_routes_${routes.length}.xlsx`);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[85vh] flex flex-col" data-testid="export-columns-dialog">
        <DialogHeader>
          <DialogTitle>Excel'e aktarılacak kolonlar</DialogTitle>
          <p className="text-xs text-slate-500">{routes.length} kayıt · {chosen.length}/{available.length} kolon seçili</p>
        </DialogHeader>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={() => setAll(true)} data-testid="export-columns-all">Tümünü seç</Button>
          <Button size="sm" variant="outline" onClick={() => setAll(false)} data-testid="export-columns-none">Hiçbiri</Button>
        </div>
        <div className="overflow-y-auto grid grid-cols-1 sm:grid-cols-2 gap-1.5 py-1 pr-1">
          {available.map((c) => (
            <label key={c.label} className="flex items-center gap-2 rounded-md border border-slate-200 dark:border-slate-700 px-2.5 py-1.5 text-sm cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800" data-testid={`export-col-${c.label}`}>
              <Checkbox checked={!excluded.includes(c.label)} onCheckedChange={() => toggle(c.label)} />
              {c.label}
            </label>
          ))}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>İptal</Button>
          <Button onClick={download} disabled={chosen.length === 0 || routes.length === 0} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="export-columns-download">
            <Download className="h-4 w-4 mr-1.5" />İndir
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
